/* eslint-disable no-template-curly-in-string */

import { TestContext, string as YupString } from 'yup'
import { IDriver, TEAM_MEMBER_ROLE_TYPES, ILoad, DriverSettingsType } from '../types'
import { ICreateLoadSchemaContext } from '../types/CreateLoadSchema'
import { IShipmentMotorCarrier } from './MotorCarrierSchema'
import { IShipmentDriver } from './ShipmentDriverSchema'

/**
 * Since context is defined by the parent `useForm` instance, make sure that
 * these functions are present before accessing them
 */
export interface IValidationContext {
  /**
   * Returns true if a user is valid type/role for booking/bidding
   */
  userCanBidAndBook?: (motorCarrierId: string | number) => boolean
  isRoleTypeForCarrier?: (
    motorCarrierId: string | number,
    role: keyof typeof TEAM_MEMBER_ROLE_TYPES
  ) => boolean
  /**
   * Returns true if selected dispatcher is associated with
   * the selected motor carrier
   */
  isAssociatedCarrier?: (dispatcherId: string | number, motorCarrierId: string | number) => boolean
  /**
   * Returns true if the current user is an admin
   * for the selected motor carrier
   */
  isAdminForCarrier?: (motorCarrierId: string | number) => {}
  isFpCarrierPortalEnabled?: boolean
  /**
   * Validate whether an empty/undefined driver value is acceptable
   */
  isDriverRequired?: boolean
}

/**
 * Returns true if the `drivers` are both associated with the user's motor carrier
 */
export function isAssociatedDriver(this: TestContext, drivers: IDriver[]) {
  const { context } = this.options as { context: IValidationContext }
  const { isAdminForCarrier, isFpCarrierPortalEnabled = false } = context || {}
  if (!isFpCarrierPortalEnabled) return true

  const [firstDriver] = drivers || []
  const parentContext = this.parent as { motorCarrier: IShipmentMotorCarrier }
  const { motorCarriers = [] } = firstDriver || {}
  const { motorCarrier } = parentContext || {}
  const matchingCarrier = motorCarriers.find(mc => mc.id === motorCarrier.id)
  const isAssociated = !!matchingCarrier

  // If there's no driver required in this form, don't validate
  if (!firstDriver) {
    return true
  }

  // make sure this function is defined
  if (typeof isAdminForCarrier !== 'function') return true

  // Does this user have 'admin' level permission for the carrier
  const hasPermission = isAdminForCarrier(motorCarrier.id)
  if (hasPermission) return true

  /**
   * If driver is not associated with this carrier, return an error
   */
  return !isAssociated
    ? this.createError({
        message:
          'This driver doesn’t have permission to manage loads for this carrier. To give them permission, you need to be an Admin for this carrier.',
      })
    : true
}

/**
 * Returns true if `dispatcher` is associated with the user's motor carrier
 */
export function isAssociatedDispatcher(this: TestContext, dispatcherId: string) {
  const { context } = (this.options || {}) as { context: ICreateLoadSchemaContext }
  const { motorCarrierId } = (this.parent || {}) as { motorCarrierId: number }
  const { isAdminForCarrier, isAssociatedCarrier, isFpCarrierPortalEnabled = false } = context || {}

  // Only enable this when `fpCarrierPortal` feature flag is enabled
  if (!isFpCarrierPortalEnabled) {
    return true
  }

  // If these functions are undefined, skip this validation
  if (!isAssociatedCarrier || !isAdminForCarrier) {
    return true
  }

  // If this user is an admin, skip this validation
  const isAdmin = isAdminForCarrier(motorCarrierId)
  if (isAdmin) {
    return true
  }

  // If motorCarrierId is not yet defined, don't validate
  if (!motorCarrierId) {
    return true
  }

  // Return true if there's an association
  return isAssociatedCarrier(dispatcherId, motorCarrierId)
}

/**
 * Returns true if there's at least 1 driver with a valid id
 */
export function hasDriver(this: TestContext, val: IShipmentDriver[]) {
  const { context } = this.options as { context: IValidationContext }
  const { isDriverRequired = false, isFpCarrierPortalEnabled = false } = context || {}
  if (!isFpCarrierPortalEnabled || !isDriverRequired) return true

  const validDrivers = val.filter(d => !!d).map(d => d?.id)

  // make sure a driver has been assigned to the load
  return validDrivers.length > 0
}

export interface IDriverFormContext {
  isExternal: ILoad['isExternal']
}

export function isDriverPhoneRequired(this: TestContext, phoneNumber: string): boolean {
  const { settings } = (this.parent || {}) as { settings: DriverSettingsType }
  const { smsStatusUpdatesEnabled = false } = settings || {}
  const { context } = this.options as {
    context: IDriverFormContext
  }
  const { isExternal } = context || {}
  const isRequired = isExternal || smsStatusUpdatesEnabled
  return isRequired
    ? YupString().required('Please enter a cellphone number').isValidSync(phoneNumber)
    : true
}
